<template>
  <div class="movimentacoes-wrap">
    <div v-if="!items.length" class="text-center text-muted py-4">
      Nenhuma movimentação encontrada.
    </div>

    <div
      v-for="dia in items"
      :key="dia.data"
      class="dia-card mb-3"
      :class="statusDiaClass(dia.status)"
    >
      <button
        type="button"
        class="dia-header"
        @click="toggleDia(dia.data)"
      >
        <div>
          <div class="dia-title">
            {{ formatDate(dia.data) }} - {{ dia.diaSemana }}
          </div>

          <div class="dia-subtitle">
            {{ statusDiaLabel(dia.status) }}
          </div>
        </div>

        <div class="dia-resumo">
          <div>
            <span>Sistema</span>
            <strong>{{ formatCurrency(dia.totalSistema) }}</strong>
          </div>

          <div>
            <span>Banco</span>
            <strong>{{ formatCurrency(dia.totalBanco) }}</strong>
          </div>

          <div>
            <span>Diferença</span>
            <strong :class="Math.abs(Number(dia.diferenca || 0)) <= 0.01 ? 'diff-ok' : 'diff-alert'">
              {{ formatCurrency(dia.diferenca) }}
            </strong>
          </div>

          <i
            class="bi"
            :class="isAberto(dia.data) ? 'bi-chevron-up' : 'bi-chevron-down'"
          ></i>
        </div>
      </button>

      <div v-if="isAberto(dia.data)" class="dia-body">

        <div
            v-if="temCausasDiferenca(dia)"
            class="causas-box mb-3"
        >
            <div class="causas-header">
                <div>
                    <strong>{{ tituloBlocoCausas(dia) }}</strong>
                    <small>
                        {{ subtituloBlocoCausas(dia) }}
                    </small>
                </div>

                <span
                    class="status-badge"
                    :class="Math.abs(Number(dia.diferenca || 0)) <= 0.01 ? 'is-green' : 'is-amber'"
                >
                    {{ Math.abs(Number(dia.diferenca || 0)) <= 0.01 ? 'Saldo conferido' : `Diferença ${formatCurrency(dia.diferenca)}` }}
                </span>
            </div>

            <div class="origem-diferenca-box mt-2">
                {{ textoOrigemDiferenca(dia) }}
            </div>            

            <div
                v-if="deveMostrarAlertaDirecionado(dia)"
                class="causas-alerta-direcionado mt-2"
            >
                {{ textoAlertaDirecionado(dia) }}
            </div>
            
            <div
                v-if="Math.abs(Number(dia.diferenca || 0)) > 0.01 && getCausaPrincipalSistema(dia)"
                class="causa-principal-box mt-3"
            >
                <div class="causa-principal-header">
                    <div>
                    <strong>Possível causa principal da diferença</strong>
                    <small>
                        Este lançamento do sistema possui valor igual à diferença do dia.
                    </small>
                    </div>

                    <span class="status-badge is-blue">
                    {{ formatCurrency(dia.diferenca) }}
                    </span>
                </div>

                <div class="causa-principal-item mt-2">
                    <div>
                    <strong>
                        {{ getCausaPrincipalSistema(dia).descricao || getCausaPrincipalSistema(dia).origem_tabela }}
                    </strong>

                    <small class="text-muted d-block">
                        {{ getCausaPrincipalSistema(dia).origem_tabela }}
                        #{{ getCausaPrincipalSistema(dia).origem_id }}
                    </small>

                    <small
                        v-if="getCausaPrincipalSistema(dia).forma_pagamento_nome"
                        class="text-muted d-block"
                    >
                        {{ getCausaPrincipalSistema(dia).forma_pagamento_nome }}
                    </small>

                    <small
                        v-if="getCausaPrincipalSistema(dia).numero_autorizacao"
                        class="text-muted d-block"
                    >
                        Autorização {{ getCausaPrincipalSistema(dia).numero_autorizacao }}
                    </small>
                    </div>

                    <div class="causa-principal-valor" :class="valorSistemaClass(getCausaPrincipalSistema(dia))">
                    {{ formatCurrency(getCausaPrincipalSistema(dia).valor) }}
                    </div>
                </div>
            </div>            

            <div
                v-if="deveMostrarCardsPendencias(dia)"
                class="causas-grid mt-3"
            >

                <div class="causa-card">
                    <div class="causa-card-title">
                        Sistema não conciliado
                        <span>{{ getSistemaNaoConciliados(dia).length }} item(ns)</span>
                    </div>

                    <div
                        v-if="!getSistemaNaoConciliados(dia).length"
                        class="causa-empty"
                    >
                        Nenhum lançamento do sistema pendente.
                    </div>

                    <div
                        v-for="item in getSistemaNaoConciliados(dia)"
                        :key="`sistema-pendente-${item.origem_tabela}-${item.origem_id}`"
                        class="causa-item"
                    >
                        <div>
                        <strong>{{ item.descricao || item.origem_tabela }}</strong>
                        <small class="text-muted d-block">
                            {{ item.origem_tabela }} #{{ item.origem_id }}
                        </small>
                        </div>

                        <span :class="valorSistemaClass(item)">
                        {{ formatCurrency(item.valor) }}
                        </span>
                    </div>
                </div>


                <div class="causa-card">
                    <div class="causa-card-title">
                        Banco pendente
                        <span>{{ getBancoPendentes(dia).length }} item(ns)</span>
                    </div>

                    <div
                        v-if="!getBancoPendentes(dia).length"
                        class="causa-empty"
                    >
                        Nenhum movimento bancário pendente.
                    </div>

                    <div
                        v-for="item in getBancoPendentes(dia)"
                        :key="`banco-pendente-${item.id}`"
                        class="causa-item"
                    >
                        <div>
                        <strong>{{ item.memo || 'Movimento bancário' }}</strong>
                        <small class="text-muted d-block">
                            OFX #{{ item.id }} · {{ formatTipoMovimento(item.tipo_movimento) }}
                        </small>
                        </div>

                        <span :class="valorBancoClass(item)">
                        {{ formatCurrency(item.valor) }}
                        </span>
                    </div>
                </div>

            </div>

            <div
                v-if="deveMostrarCardsPendencias(dia) && getPossiveisComposicoes(dia).length"
                class="composicoes-box mt-3"
            >
                <div class="composicoes-title">
                Possíveis composições encontradas
                </div>

                <div
                v-for="comp in getPossiveisComposicoes(dia)"
                :key="`comp-${comp.banco.id}`"
                class="composicao-item"
                >
                <div class="composicao-banco">
                    <div>
                    <strong>{{ comp.banco.memo || 'Movimento bancário' }}</strong>
                    <small class="text-muted d-block">
                        OFX #{{ comp.banco.id }}
                    </small>
                    </div>

                    <span :class="valorBancoClass(comp.banco)">
                    {{ formatCurrency(comp.banco.valor) }}
                    </span>
                </div>

                <div class="composicao-seta">
                    pode fechar com
                </div>

                <div class="composicao-sistema">
                    <div
                    v-for="item in comp.sistema"
                    :key="`comp-sis-${item.origem_tabela}-${item.origem_id}`"
                    class="composicao-sistema-item"
                    >
                    <div>
                        <strong>{{ item.descricao || item.origem_tabela }}</strong>
                        <small class="text-muted d-block">
                        {{ item.origem_tabela }} #{{ item.origem_id }}
                        </small>
                    </div>

                    <span :class="valorSistemaClass(item)">
                        {{ formatCurrency(item.valor) }}
                    </span>
                    </div>
                </div>
                </div>
            </div>
        </div>

        <div class="mov-grid">
          <div class="mov-column">
            <div class="mov-column-title">
              Lançado no sistema
              <span>{{ dia.sistema?.length || 0 }} item(ns)</span>
            </div>

            <div v-if="!dia.sistema?.length" class="empty-box">
              Nenhum lançamento do sistema neste dia.
            </div>

            <div
              v-for="mov in dia.sistema"
              :key="`${mov.origem_tabela}-${mov.origem_id}`"
              class="mov-item"
            >
              <div class="mov-main">
                <div>
                  <strong>{{ mov.descricao || mov.origem_tabela }}</strong>

                  <small class="text-muted d-block">
                    {{ mov.origem_tabela }} #{{ mov.origem_id }}
                  </small>

                  <small class="text-muted d-block">
                    {{ mov.forma_pagamento_nome || 'Forma não informada' }}
                  </small>
                </div>

                <div class="mov-valor" :class="valorSistemaClass(mov)">
                  {{ formatCurrency(mov.valor) }}
                </div>
              </div>

              <div class="mov-meta mt-2">
                <span
                class="status-badge"
                :class="mov.conciliado ? 'is-green' : 'is-yellow'"
                >
                {{ statusSistemaLabel(mov) }}
                </span>

                <span
                  v-if="mov.vinculo_resumo"
                  class="status-badge is-blue"
                >
                  {{ mov.vinculo_resumo }}
                </span>

                <span
                  v-if="mov.numero_autorizacao"
                  class="status-badge is-gray"
                >
                  Aut. {{ mov.numero_autorizacao }}
                </span>
              </div>
            </div>
          </div>

          <div class="mov-column">
            <div class="mov-column-title">
              Movimentado no banco
              <span>{{ dia.banco?.length || 0 }} item(ns)</span>
            </div>

            <div v-if="!dia.banco?.length" class="empty-box">
              Nenhuma movimentação bancária neste dia.
            </div>

            <div
              v-for="mov in dia.banco"
              :key="mov.id"
              class="mov-item"
            >
              <div class="mov-main">
                <div>
                  <strong>{{ mov.memo || 'Movimento bancário' }}</strong>

                  <small class="text-muted d-block">
                    OFX #{{ mov.id }} · {{ formatTipoMovimento(mov.tipo_movimento) }}
                  </small>

                  <small class="text-muted d-block">
                    {{ formatTipoBancario(mov.tipo_bancario) }}
                  </small>
                </div>

                <div class="mov-valor" :class="valorBancoClass(mov)">
                  {{ formatCurrency(mov.valor) }}
                </div>
              </div>

              <div class="mov-meta-row mt-2">
                <div class="mov-meta">
                  <span
                    class="status-badge"
                    :class="statusBancoClass(mov)"
                  >
                    {{ statusBancoLabel(mov) }}
                  </span>

                  <span
                    v-if="mov.movimento_label_amigavel"
                    class="status-badge is-blue"
                  >
                    {{ formatarVinculoResumo(mov.movimento_label_amigavel) }}
                  </span>

                  <span
                    v-else-if="mov.movimento_label"
                    class="status-badge is-blue"
                  >
                    {{ formatarVinculoSistemaLabel(mov.movimento_label) }}
                  </span>

                  <span
                    v-else-if="mov.banco_operadora_vinculo_id"
                    class="status-badge is-purple"
                  >
                    {{ mov.banco_operadora_operadora_nome || 'Operadora' }}
                    #{{ mov.banco_operadora_operadora_item_id || mov.banco_operadora_vinculo_id }}
                  </span>
                </div>

                <div class="mov-actions">
                  <template v-if="podeConciliarBanco(mov)">
                    <i
                      class="bi bi-link-45deg text-primary mx-1 action-icon"
                      title="Conciliar"
                      @click.stop="emit('conciliar', {
                        origemTipo: 'OFX',
                        item: enriquecerItemComComposicao(dia, mov)
                      })"
                    ></i>
                  </template>

                  <template v-else>
                    <i
                      class="bi bi-eye-fill text-primary mx-1 action-icon"
                      title="Detalhar"
                      @click.stop="emit('detalhar', { origemTipo: 'OFX', item: mov })"
                    ></i>

                    <i
                      class="bi bi-arrow-counterclockwise text-danger mx-1 action-icon"
                      title="Desfazer"
                      @click.stop="emit('desfazer', { origemTipo: 'OFX', item: mov })"
                    ></i>
                  </template>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div class="dia-footer mt-3">
          <div>
            <span>Total sistema</span>
            <strong>{{ formatCurrency(dia.totalSistema) }}</strong>
          </div>

          <div>
            <span>Total banco</span>
            <strong>{{ formatCurrency(dia.totalBanco) }}</strong>
          </div>

          <div>
            <span>Diferença do dia</span>
            <strong :class="Math.abs(Number(dia.diferenca || 0)) <= 0.01 ? 'diff-ok' : 'diff-alert'">
              {{ formatCurrency(dia.diferenca) }}
            </strong>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

defineProps({
  items: { type: Array, default: () => [] }
})

const emit = defineEmits(['conciliar', 'detalhar', 'desfazer'])

const diasAbertos = ref({})

function toggleDia(data) {
  diasAbertos.value[data] = !diasAbertos.value[data]
}

function isAberto(data) {
  return Boolean(diasAbertos.value[data])
}

function formatDate(v) {
  if (!v) return '—'
  const [y, m, d] = String(v).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}

function formatTipoMovimento(v) {
  const tipo = String(v || '').toUpperCase().trim()

  if (tipo === 'DEBIT') return 'Débito'
  if (tipo === 'CREDIT') return 'Crédito'

  return v || '—'
}

function formatTipoBancario(v) {
  const map = {
    PIX_RECEBIDO: 'Pix recebido',
    PIX_ENVIADO: 'Pix enviado',
    CARTAO_GETNET_CREDITO: 'Getnet crédito',
    CARTAO_GETNET_DEBITO: 'Getnet débito',
    CHEQUE_DEPOSITO: 'Cheque depósito',
    CHEQUE_DEVOLVIDO: 'Cheque devolvido',
    CHEQUE_REAPRESENTADO: 'Cheque reapresentado',
    BOLETO: 'Boleto',
    DARF: 'DARF',
    APLICACAO: 'Aplicação',
    RESGATE_APLICACAO: 'Resgate aplicação',
    SALARIOS: 'Salários',
    TARIFA_BANCARIA: 'Tarifa bancária',
    TRANSFERENCIA_BANCARIA: 'TED/DOC',
    TRANSFERENCIA_INTERNA: 'Transferência interna',
    DEBITO_AUTOMATICO: 'Débito automático',
    OUTRO: 'Outro'
  }

  const key = String(v || '').toUpperCase()
  return map[key] || v || 'Outro'
}

function statusDiaLabel(status) {
  const map = {
    CONFERIDO: 'Sistema e banco conferem',
    COM_DIFERENCA: 'Saldo com diferença',
    SEM_LANCAMENTO_SISTEMA: 'Movimento no banco sem lançamento no sistema',
    SEM_MOVIMENTO_BANCO: 'Lançamento no sistema sem movimento no banco'
  }

  return map[String(status || '').toUpperCase()] || status || '—'
}

function statusDiaClass(status) {
  const key = String(status || '').toUpperCase()

  if (key === 'CONFERIDO') return 'dia-card--ok'
  if (key === 'COM_DIFERENCA') return 'dia-card--alert'
  if (key === 'SEM_LANCAMENTO_SISTEMA') return 'dia-card--warning'
  if (key === 'SEM_MOVIMENTO_BANCO') return 'dia-card--info'

  return ''
}

function valorSistemaClass(item) {
  const tipo = String(item?.tipo || '').toUpperCase()

  if (tipo === 'RECEITA') return 'valor-credito'
  if (tipo === 'DESPESA') return 'valor-debito'

  return ''
}

function valorBancoClass(item) {
  const valor = Number(item?.valor || 0)

  if (valor > 0) return 'valor-credito'
  if (valor < 0) return 'valor-debito'

  return ''
}

function statusBancoLabel(item) {
  const status = String(item?.status_conciliacao || '').toUpperCase()

  if (item?.banco_operadora_vinculo_id) return 'Conciliado Banco x Operadora'
  if (status === 'CONCILIADO_AUTOMATICO') return 'Conciliado automático'
  if (status === 'CONCILIADO_MANUAL') return 'Conciliado manual'
  if (status === 'PENDENTE') return 'Pendente'

  return status.replaceAll('_', ' ') || '—'
}

function statusBancoClass(item) {
  const status = String(item?.status_conciliacao || '').toUpperCase()

  if (item?.banco_operadora_vinculo_id) return 'is-purple'
  if (status === 'CONCILIADO_AUTOMATICO') return 'is-green'
  if (status === 'CONCILIADO_MANUAL') return 'is-blue'
  if (status === 'PENDENTE') return 'is-yellow'

  return 'is-gray'
}

function diferencaAbsValor(dia) {
  return Math.abs(Number(Number(dia?.diferenca || 0).toFixed(2)))
}

function temCausasDiferenca(dia) {
  if (!dia) return false

  const diff = diferencaAbsValor(dia)
  const bancoPendentes = getBancoPendentes(dia).length
  const sistemaPendentes = getSistemaNaoConciliados(dia).length

  if (diff > 0 && diff <= 0.01) return true
  if (diff > 0.01) return true
  if (bancoPendentes > 0) return true
  if (sistemaPendentes > 0) return true

  return false
}

function deveMostrarCardsPendencias(dia) {
  return (
    getBancoPendentes(dia).length > 0 ||
    getSistemaNaoConciliados(dia).length > 0
  )
}

function getBancoPendentes(dia) {
  return (dia?.banco || []).filter((item) => !item?.conciliado)
}

function getSistemaNaoConciliados(dia) {
  return (dia?.sistema || []).filter((item) => {
    if (item?.conciliado) return false
    if (item?.taxa_operadora_automatica) return false
    if (item?.ajuste_operadora_automatico) return false

    const valor = Math.abs(Number(item?.valor || 0))
    if (valor <= 0.01) return false

    return true
  })
}

function getCausaPrincipalSistema(dia) {
  if (!dia) return null

  const bancoPendentes = getBancoPendentes(dia)

  if (bancoPendentes.length > 0) {
    return null
  }

  const diferencaAbs = Math.abs(Number(dia.diferenca || 0))

  if (diferencaAbs <= 0.01) {
    return null
  }

  const candidatos = getSistemaNaoConciliados(dia)

  return candidatos.find((item) => {
    const valorAbs = Math.abs(Number(item?.valor || 0))
    return Math.abs(valorAbs - diferencaAbs) <= 0.01
  }) || null
}

function getPossiveisComposicoes(dia) {
  const bancoPendentes = getBancoPendentes(dia)
  const sistemaPendentes = getSistemaNaoConciliados(dia)

  if (!bancoPendentes.length || !sistemaPendentes.length) {
    return []
  }

  const composicoes = []

  for (const banco of bancoPendentes) {
    const valorBanco = Number(banco?.valor || 0)

    if (Math.abs(valorBanco) <= 0.01) continue

    const candidatosMesmoSinal = sistemaPendentes.filter((item) => {
      const valorSistema = Number(item?.valor || 0)

      if (Math.abs(valorSistema) <= 0.01) return false

      return (
        (valorBanco < 0 && valorSistema < 0) ||
        (valorBanco > 0 && valorSistema > 0)
      )
    })

    const combinacao = encontrarCombinacaoPorValor(
      candidatosMesmoSinal,
      Math.abs(valorBanco)
    )

    if (combinacao.length) {
      composicoes.push({
        banco,
        sistema: combinacao
      })
    }
  }

  return composicoes
}

function encontrarCombinacaoPorValor(itens, valorAlvo) {
  const alvoCentavos = Math.round(Math.abs(Number(valorAlvo || 0)) * 100)

  if (!alvoCentavos) return []

  const lista = (itens || [])
    .map((item) => ({
      ...item,
      valorCentavos: Math.round(Math.abs(Number(item.valor || 0)) * 100)
    }))
    .filter((item) => item.valorCentavos > 0 && item.valorCentavos <= alvoCentavos)
    .sort((a, b) => b.valorCentavos - a.valorCentavos)

  const resultado = []
  const memo = new Set()

  function backtrack(index, soma) {
    const chave = `${index}-${soma}`

    if (memo.has(chave)) return false
    memo.add(chave)

    if (soma === alvoCentavos) {
      return true
    }

    if (soma > alvoCentavos) {
      return false
    }

    for (let i = index; i < lista.length; i += 1) {
      const item = lista[i]

      resultado.push(item)

      if (backtrack(i + 1, soma + item.valorCentavos)) {
        return true
      }

      resultado.pop()
    }

    return false
  }

    if (backtrack(0, 0)) {
    return resultado.map((item) => {
        const clone = { ...item }
        delete clone.valorCentavos
        return clone
    })
    }

    return []
}

function statusSistemaLabel(item) {
  if (item?.ajuste_operadora_automatico) {
    return 'Ajuste da operadora'
  }

  if (item?.taxa_operadora_automatica) {
    return 'Taxa da operadora'
  }

  if (item?.conciliado) {
    return 'Conciliado'
  }

  return 'Não conciliado'
}

function diferencaZerada(dia) {
  return Math.abs(Number(dia?.diferenca || 0)) <= 0.01
}

function tituloBlocoCausas(dia) {
  const diff = diferencaAbsValor(dia)
  const bancoPendentes = getBancoPendentes(dia).length
  const sistemaPendentes = getSistemaNaoConciliados(dia).length

  if (diff > 0 && diff <= 0.01 && bancoPendentes === 0 && sistemaPendentes === 0) {
    return 'Diferença residual'
  }

  if (diferencaZerada(dia)) {
    return 'Pendências de conciliação do dia'
  }

  return 'Possíveis causas da diferença'
}

function subtituloBlocoCausas(dia) {
  const diff = diferencaAbsValor(dia)
  const bancoPendentes = getBancoPendentes(dia).length
  const sistemaPendentes = getSistemaNaoConciliados(dia).length

  if (diff > 0 && diff <= 0.01 && bancoPendentes === 0 && sistemaPendentes === 0) {
    return 'O saldo está conferido dentro da tolerância.'
  }

  if (diferencaZerada(dia)) {
    return 'O saldo do dia confere, mas ainda existem lançamentos sem vínculo de conciliação.'
  }

  return 'Itens ainda não conciliados ou composições que podem explicar o saldo do dia.'
}

function deveMostrarAlertaDirecionado(dia) {
  const diff = Math.abs(Number(dia?.diferenca || 0))
  const bancoPendentes = getBancoPendentes(dia).length
  const sistemaPendentes = getSistemaNaoConciliados(dia).length

  if (diff <= 0.01) return false
  if (bancoPendentes > 0) return false

  return sistemaPendentes > 0
}

function textoAlertaDirecionado(dia) {
  if (diferencaZerada(dia)) {
    return 'Não há diferença financeira neste dia. A pendência restante é operacional: existem lançamentos do sistema ainda sem vínculo de conciliação.'
  }

  return 'Não há movimentos bancários pendentes neste dia. A diferença indica que há lançamentos no sistema sem entrada correspondente no banco. Revise data de recebimento, conta financeira ou status desses lançamentos.'
}

function enriquecerItemComComposicao(dia, itemBanco) {
  const composicoes = getPossiveisComposicoes(dia)

  const composicao = composicoes.find((comp) =>
    Number(comp?.banco?.id) === Number(itemBanco?.id)
  )

  if (!composicao) {
    return itemBanco
  }

  return {
    ...itemBanco,
    composicao_sugerida: {
      origem: 'MOVIMENTACOES_DIARIAS',
      total: Number(itemBanco?.valor || 0),
      candidatos: composicao.sistema.map((item) => ({
        origem_tabela: item.origem_tabela,
        origem_id: item.origem_id,
        descricao: item.descricao,
        valor: Number(item.valor || 0),
        data: item.data,
        forma_pagamento_nome: item.forma_pagamento_nome || null
      }))
    }
  }
}

function textoOrigemDiferenca(dia) {
  const diff = Math.abs(Number(dia?.diferenca || 0))
  const bancoPendentes = getBancoPendentes(dia)
  const sistemaPendentes = getSistemaNaoConciliados(dia)

  if (diff <= 0.01) {
    return 'Saldo conferido dentro da tolerância. Diferença residual provavelmente causada por arredondamentos de taxas, operadora ou ajustes financeiros.'
  }

  if (bancoPendentes.length > 0 && sistemaPendentes.length > 0) {
    return 'A diferença pode estar relacionada a movimentos pendentes tanto no banco quanto no sistema.'
  }

  if (bancoPendentes.length > 0) {
    return 'A diferença pode estar relacionada a movimentos bancários ainda não conciliados.'
  }

  if (sistemaPendentes.length > 0) {
    return 'A diferença indica lançamentos do sistema sem movimento bancário correspondente. Revise data de recebimento, conta financeira ou status.'
  }

  return 'Não há pendências aparentes. A diferença pode estar relacionada a arredondamentos, taxas, chargebacks, cheques devolvidos/reapresentados ou ajustes já tratados.'
}

function podeConciliarBanco(item) {
  if (!item) return false

  const status = String(item.status_conciliacao || '').toUpperCase()

  return (
    !item.vinculo_id &&
    !item.banco_operadora_vinculo_id &&
    status !== 'CONCILIADO_AUTOMATICO' &&
    status !== 'CONCILIADO_MANUAL'
  )
}

function formatarOrigemSistema(origem) {
  const key = String(origem || '').trim()

  const map = {
    aulas_avulsas_pagamentos: 'Aula avulsa',
    matriculas_pagamentos: 'Matrícula',
    pagamentos_matriculas: 'Mensalidade',
    prospeccao_pagamentos: 'Aula experimental',
    receitas_gerais_pagamentos: 'Receita',
    vendas_pagamentos: 'Venda da loja',
    despesas_pagamentos: 'Despesa',
    entradas_pagamentos: 'Entrada/Compra',
    transferencias_contas_movimentos: 'Transferência',
    matricula_renovacoes_pagamentos: 'Renovação',
    matricula_trocas_planos_pagamentos: 'Troca de plano'
  }

  return map[key] || key || 'Lançamento'
}

function formatarVinculoSistemaLabel(texto) {
  const valor = String(texto || '').trim()
  if (!valor) return '—'

  const match = valor.match(/^([a-zA-Z0-9_]+)\s+#?(\d+)$/)

  if (!match) return valor

  const origem = match[1]
  const id = match[2]

  return `${formatarOrigemSistema(origem)} #${id}`
}

function formatarVinculoResumo(texto) {
  const valor = String(texto || '').trim()
  if (!valor) return '—'

  const partes = valor
    .split(' + ')
    .map((p) => p.trim())
    .filter(Boolean)

  if (!partes.length) return '—'

  const primeiro = formatarVinculoSistemaLabel(partes[0])

  if (partes.length === 1) return primeiro

  const restantes = partes.length - 1
  const sufixo = restantes === 1 ? 'vínculo' : 'vínculos'

  return `${primeiro} + ${restantes} ${sufixo}`
}

</script>

<style scoped>
.movimentacoes-wrap {
  width: 100%;
}

.dia-card {
  border: 1px solid #d1d5db;
  border-left-width: 5px;
  background: #fff;
  border-radius: 0.5rem;
  overflow: hidden;
}

.dia-card--ok {
  border-left-color: #16a34a;
}

.dia-card--alert {
  border-left-color: #dc2626;
}

.dia-card--warning {
  border-left-color: #d97706;
}

.dia-card--info {
  border-left-color: #2563eb;
}

.dia-header {
  width: 100%;
  border: none;
  background: #f9fafb;
  padding: 0.85rem 1rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  text-align: left;
  align-items: center;
}

.dia-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #111827;
}

.dia-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 0.15rem;
}

.dia-resumo {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.dia-resumo > div,
.dia-footer > div {
  min-width: 120px;
  text-align: right;
}

.dia-resumo span,
.dia-footer span {
  display: block;
  font-size: 0.7rem;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 700;
}

.dia-resumo strong,
.dia-footer strong {
  font-size: 0.88rem;
}

.dia-body {
  padding: 1rem;
}

.mov-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.mov-column {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fff;
  overflow: hidden;
}

.mov-column-title {
  padding: 0.65rem 0.8rem;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.mov-column-title span {
  color: #6b7280;
  font-size: 0.76rem;
  font-weight: 600;
}

.empty-box {
  padding: 1rem;
  color: #6b7280;
  text-align: center;
  font-size: 0.85rem;
}

.mov-item {
  padding: 0.75rem 0.8rem;
  border-bottom: 1px solid #f1f5f9;
}

.mov-item:last-child {
  border-bottom: none;
}

.mov-main {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.mov-main strong {
  font-size: 0.86rem;
}

.mov-valor {
  white-space: nowrap;
  font-weight: 700;
  font-size: 0.88rem;
  text-align: right;
}

.mov-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.mov-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.dia-footer {
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 0.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
}

.status-badge {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
}

.valor-credito {
  color: #166534;
}

.valor-debito {
  color: #b91c1c;
}

.diff-ok {
  color: #166534;
}

.diff-alert {
  color: #b91c1c;
}

.is-green { background: #dcfce7; color: #166534; }
.is-blue { background: #dbeafe; color: #1d4ed8; }
.is-yellow { background: #fef3c7; color: #92400e; }
.is-amber { background: #fde68a; color: #92400e; }
.is-gray { background: #e5e7eb; color: #374151; }
.is-purple { background: #ede9fe; color: #6d28d9; }

.causas-box {
  border: 1px solid #f59e0b;
  background: #fffbeb;
  border-radius: 0.5rem;
  padding: 0.85rem;
}

.causas-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.causas-header strong {
  display: block;
  color: #92400e;
  font-size: 0.95rem;
}

.causas-header small {
  display: block;
  color: #78350f;
  margin-top: 0.15rem;
}

.causas-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.causa-card {
  background: #fff;
  border: 1px solid #fde68a;
  border-radius: 0.45rem;
  overflow: hidden;
}

.causa-card-title {
  background: #fef3c7;
  padding: 0.55rem 0.7rem;
  font-weight: 700;
  color: #78350f;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.causa-card-title span {
  font-size: 0.75rem;
  font-weight: 600;
  color: #92400e;
}

.causa-empty {
  padding: 0.75rem;
  color: #6b7280;
  font-size: 0.83rem;
  text-align: center;
}

.causa-item {
  padding: 0.65rem 0.7rem;
  border-top: 1px solid #fef3c7;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.causa-item strong {
  font-size: 0.82rem;
}

.causa-item > span {
  white-space: nowrap;
  font-weight: 700;
  font-size: 0.82rem;
}

.composicoes-box {
  border: 1px dashed #f59e0b;
  border-radius: 0.45rem;
  background: #fff;
  padding: 0.75rem;
}

.composicoes-title {
  font-weight: 700;
  color: #92400e;
  margin-bottom: 0.5rem;
}

.composicao-item {
  border-top: 1px solid #fde68a;
  padding-top: 0.65rem;
  margin-top: 0.65rem;
}

.composicao-item:first-of-type {
  border-top: none;
  padding-top: 0;
  margin-top: 0;
}

.composicao-banco,
.composicao-sistema-item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.composicao-banco {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  padding: 0.55rem 0.65rem;
  border-radius: 0.4rem;
}

.composicao-seta {
  font-size: 0.75rem;
  font-weight: 700;
  color: #92400e;
  margin: 0.45rem 0;
  text-transform: uppercase;
}

.composicao-sistema {
  display: grid;
  gap: 0.4rem;
}

.composicao-sistema-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  padding: 0.55rem 0.65rem;
  border-radius: 0.4rem;
}

.composicao-banco span,
.composicao-sistema-item span {
  white-space: nowrap;
  font-weight: 700;
}

.causas-alerta-direcionado {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 0.4rem;
  padding: 0.55rem 0.7rem;
  font-size: 0.84rem;
  font-weight: 600;
}

.causa-principal-box {
  border: 1px solid #93c5fd;
  background: #eff6ff;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.causa-principal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.causa-principal-header strong {
  display: block;
  color: #1e40af;
  font-size: 0.92rem;
}

.causa-principal-header small {
  display: block;
  color: #1d4ed8;
  margin-top: 0.15rem;
}

.causa-principal-item {
  background: #fff;
  border: 1px solid #bfdbfe;
  border-radius: 0.45rem;
  padding: 0.65rem 0.75rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.causa-principal-item strong {
  font-size: 0.86rem;
}

.causa-principal-valor {
  white-space: nowrap;
  font-weight: 700;
  font-size: 0.9rem;
  text-align: right;
}

.origem-diferenca-box {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 0.4rem;
  padding: 0.55rem 0.7rem;
  font-size: 0.84rem;
  font-weight: 600;
}

.action-icon {
  cursor: pointer;
  font-size: 1.05rem;
  vertical-align: middle;
}

.action-icon:hover {
  opacity: 0.75;
}

.mov-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  flex-wrap: wrap;
  align-items: center;
}

.mov-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.mov-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
  min-width: 0;
}

.mov-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  align-items: center;
  flex-shrink: 0;
}

.action-icon {
  cursor: pointer;
  font-size: 1.05rem;
  vertical-align: middle;
}

.action-icon:hover {
  opacity: 0.75;
}

@media (max-width: 576px) {
  .mov-meta-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .mov-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 992px) {
  .causa-principal-item {
    flex-direction: column;
  }

  .causa-principal-valor {
    text-align: left;
  }
}

@media (max-width: 992px) {
  .causas-grid {
    grid-template-columns: 1fr;
  }

  .causa-item,
  .composicao-banco,
  .composicao-sistema-item {
    flex-direction: column;
  }
}

@media (max-width: 992px) {
  .mov-grid {
    grid-template-columns: 1fr;
  }

  .dia-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .dia-resumo {
    width: 100%;
    justify-content: space-between;
  }

  .dia-resumo > div,
  .dia-footer > div {
    text-align: left;
  }
}
</style>